<!-- =========================================================================================
  File Name: DashboardAnalytics.vue
  Description: Dashboard Analytics
  ----------------------------------------------------------------------------------------
  Item Name: Vuexy - Vuejs, HTML & Laravel Admin Dashboard Template
  Author: Pixinvent
  Author URL: http://www.themeforest.net/user/pixinvent
========================================================================================== -->

<template>
  <div id="dashboard-analytics">
    <statistics-cards :data="statisticalData"/>
    <div class="vx-row">

      <div class="vx-col w-full lg:w-1/3 mb-base">
        <vx-card>
          <vue-apex-charts
            type="bar"
            height="350"
            :options="createdBarChartOptions"
            :series="reportData.createdThisWeek"
          ></vue-apex-charts>
        </vx-card>
      </div>
      <div class="vx-col w-full lg:w-1/3 mb-base">
        <vx-card>
          <vue-apex-charts
            type="bar"
            height="350"
            :options="updatedBarChartOptions"
            :series="reportData.updatedThisWeek"
          ></vue-apex-charts>
        </vx-card>
      </div>
      <div class="vx-col w-full lg:w-1/3 mb-base">
        <vx-card>
          <vue-apex-charts
            type="bar"
            height="350"
            :options="topTenBarChartOptions"
            :series="reportData.topTenCompanies"
          ></vue-apex-charts>
        </vx-card>
      </div>
    </div>
  </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts'
import StatisticsCardLine from '@/components/statistics-cards/StatisticsCardLine.vue'
import StatisticsCards from '@/components/statistics-cards/StatisticsCards.vue'
import analyticsData from './ui-elements/card/analyticsData.js'
import ChangeTimeDurationDropdown from '@/components/ChangeTimeDurationDropdown.vue'
import VxTimeline from '@/components/timeline/VxTimeline'

export default {
  data () {
    return {
      reportData: {
        numContacts: 0,
        numUsers: 0,
        numCompanies: 0,
        numApplications: 0,
        numRoles: 0,
        createdThisWeek: [{
          name: 'Created',
          data: [],
        }],
        updatedThisWeek: [{
          name: 'Updated',
          data: [],
        }],
        topTenCompanies: [{
          name: 'Top Ten Entities',
          data: [],
        }]
      },
      barChartColours: ['#f9db2b', '#FF8307', '#626262'],
      barChartOptions: {
        plotOptions: {
          bar: {
            columnWidth: '45%',
            distributed: true
          }
        },
        dataLabels: {
          enabled: false
        },
        legend: {
          show: false
        },
      },
      createdBarChartOptions: {},
      updatedBarChartOptions: {},
      topTenBarChartOptions: {},
    }
  },
  components: {
    VueApexCharts,
    StatisticsCardLine,
    ChangeTimeDurationDropdown,
    VxTimeline,
    StatisticsCards,
  },
  computed: {
    statisticalData() {
      return {
        numberOfContacts: {
          icon: 'address-card',
          label: 'Number of Contacts',
          value: this.reportData.numContacts,
        },
        numberOfUsers: {
          icon: 'users',
          label: 'Number of Users',
          value: this.reportData.numUsers,
        },
        numberOfEntities: {
          icon: 'building',
          label: 'Number of Entities',
          value: this.reportData.numCompanies,
        },
        numberOfApplications: {
          icon: 'browser',
          label: 'Number of Applications',
          value: this.reportData.numApplications,
        },
        numberOfRoles: {
          icon: 'user-tag',
          label: 'Number of Roles',
          value: this.reportData.numRoles,
        }
      };
    },
  },
  created () {
    this.createdBarChartOptions = {
    ...this.barChartOptions,
      chart: {
        height: 400,
        type: 'bar',
      },
      title: {
        text: 'Created this week',
        align: 'left'
      },
      colors: this.barChartColours,
      xaxis: {
        categories: [
          ['Users'],
          ['Contacts'],
          ['Entities'],
        ],
        labels: {
          style: {
            fontSize: '12px'
          }
        }
      },
    };
    this.updatedBarChartOptions = {
      ...this.barChartOptions,
      chart: {
        height: 400,
        type: 'bar',
      },
      title: {
        text: 'Updated this week',
        align: 'left'
      },
      colors: this.barChartColours,
      xaxis: {
        categories: [
          ['Users'],
          ['Contacts'],
          ['Entities'],
        ],
        labels: {
          style: {
            fontSize: '12px'
          }
        }
      },
    };
    this.topTenBarChartOptions = {
      ...this.barChartOptions,
      chart: {
        id: 'topten',
        height: 400,
        type: 'bar',
      },
      title: {
        text: 'Top 10 entities by user',
        align: 'left'
      },
      colors: [
        '#1A1A1A',
        '#f9db2b',
        '#FF8307',
        '#626262',
        '#f9db2b',
        '#FF8307',
        '#626262',
        '#f9db2b',
        '#FF8307',
        '#626262',
      ],
      plotOptions: {
        bar: {
          distributed: true,
          horizontal: true,
          barHeight: '75%',
          dataLabels: {
            position: 'bottom'
          }
        }
      },
      xaxis: {
        categories: [],
        labels: {
          style: {
            fontSize: '12px'
          }
        }
      },
    };

    this.fetchData();
  },
  methods: {
    async fetchData() {
      try {
        const { data } = await this.$api.reports.list();
        const {
          numContacts,
          numUsers,
          numCompanies,
          numApplications,
          numRoles,
          createdThisWeek,
          updatedThisWeek,
          topTenCompanies
        } = data.data;

        this.reportData = {
          ...this.reportData,
          numContacts,
          numUsers,
          numCompanies,
          numApplications,
          numRoles,
          createdThisWeek: [{
            data: Object.values(createdThisWeek)
          }],
          updatedThisWeek: [{
            data: Object.values(updatedThisWeek)
          }],
        }

        let companies = [];
        let labels = [];

        topTenCompanies.forEach((item) => {
          companies.push(item.users_count);
          labels.push([item.CompanyName]);
        });

        ApexCharts.exec('topten', "updateOptions", {
          xaxis: {
            categories: labels
          }
        });

        ApexCharts.exec('topten', "updateSeries", [
          {
            data: companies
          }
        ]);
      } catch (e) {
        console.error(e)

      }
    }
  }
}
</script>

<style lang="scss">
/*! rtl:begin:ignore */
#dashboard-analytics {
  .greet-user{
    position: relative;

    .decore-left{
      position: absolute;
      left:0;
      top: 0;
    }
    .decore-right{
      position: absolute;
      right:0;
      top: 0;
    }
  }

  @media(max-width: 576px) {
    .decore-left, .decore-right{
      width: 140px;
    }
  }
}
/*! rtl:end:ignore */
</style>
